import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { Plus } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import { SortableContext, arrayMove, horizontalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { Button, Popover, PopoverContent, PopoverTrigger } from "@nexus/ui"
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog"
import { useResolvedMyBrandKpis } from "../hooks/useResolvedMyBrandKpis"
import { getVisiblePillCount } from "../lib/kpi-pill-overflow"
import { MIN_MY_BRAND_KPIS, useDrilldownFiltersStore } from "../stores/drilldownFilters.store"
import { MyBrandKpiOverflowMenu } from "./MyBrandKpiOverflowMenu"
import { MyBrandKpiPill } from "./MyBrandKpiPill"

const PILL_ROW_GAP_FALLBACK_PX = 8
// Wider than a two-digit "+N" plus the trigger's padding, so the last visible
// pill is not clipped before the trigger itself has been measured.
const OVERFLOW_TRIGGER_RESERVE_PX = 56

/** My Brand's KPI order-by row: replaces the competitors-only "Order by" dropdown.
 *  Each pill both picks a visible list-view column and can drive the active sort;
 *  drag-reorders the pills (and matching table columns); "+" adds another KPI.
 *  Pills that do not fit stay on one line behind a "+N" menu. */
export function MyBrandKpiPillRow() {
  const { t } = useTranslation()
  // Options come from the caller's own KPI settings; `myBrandById` is org-union-user
  // with user overrides winning so a user-created KPI still resolves for the pill and
  // the grid cells.
  const { myBrandCatalog, myBrandById } = useKpiSelectorCatalog("drilldown")
  // Ghost ids (selected, but absent from this tenant's catalog) have no pill.
  // Removal and the add list follow the pills the user can actually see.
  const resolvedKpis = useResolvedMyBrandKpis()
  const myBrandSortMode = useDrilldownFiltersStore((s) => s.myBrandSortMode)
  const myBrandSortDirection = useDrilldownFiltersStore((s) => s.myBrandSortDirection)
  const setMyBrandKpis = useDrilldownFiltersStore((s) => s.setMyBrandKpis)
  const addMyBrandKpi = useDrilldownFiltersStore((s) => s.addMyBrandKpi)
  const removeMyBrandKpi = useDrilldownFiltersStore((s) => s.removeMyBrandKpi)
  const replaceMyBrandKpi = useDrilldownFiltersStore((s) => s.replaceMyBrandKpi)
  const toggleMyBrandKpiSort = useDrilldownFiltersStore((s) => s.toggleMyBrandKpiSort)

  // A click that doesn't move the pointer at least 5px is a click, not a drag —
  // without this, PointerSensor can swallow clicks on the name-dropdown/arrow/X.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const labelsKey = resolvedKpis.map((id) => `${id}:${myBrandById[id]?.label ?? ""}`).join("|")
  const { containerRef, addButtonRef, shownIds, hiddenIds } = useSingleLineKpiIds(resolvedKpis, labelsKey)

  const available = myBrandCatalog.filter((e) => !resolvedKpis.includes(e.id))
  const canRemove = resolvedKpis.length > MIN_MY_BRAND_KPIS

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = resolvedKpis.indexOf(active.id as string)
    const newIndex = resolvedKpis.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return
    setMyBrandKpis(arrayMove(resolvedKpis, oldIndex, newIndex))
  }

  return (
    <div
      ref={containerRef}
      data-testid="my-brand-kpi-pill-row"
      className="flex min-w-0 flex-1 flex-nowrap items-center justify-end gap-2 overflow-hidden"
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={shownIds} strategy={horizontalListSortingStrategy}>
          {shownIds.map((id) => {
            const entry = myBrandById[id]
            if (!entry) return null
            return (
              <MyBrandKpiPill
                key={id}
                id={id}
                entry={entry}
                catalog={myBrandCatalog}
                isActiveSort={myBrandSortMode === id}
                sortDirection={myBrandSortDirection}
                canRemove={canRemove}
                selectedIds={resolvedKpis}
                onToggleSort={toggleMyBrandKpiSort}
                onRemove={(id) => removeMyBrandKpi(id, resolvedKpis)}
                onReplace={replaceMyBrandKpi}
              />
            )
          })}
        </SortableContext>
      </DndContext>

      <MyBrandKpiOverflowMenu
        ids={hiddenIds}
        byId={myBrandById}
        sortMode={myBrandSortMode}
        sortDirection={myBrandSortDirection}
        canRemove={canRemove}
        onToggleSort={toggleMyBrandKpiSort}
        onRemove={removeMyBrandKpi}
      />

      <Popover>
        <PopoverTrigger asChild>
          <button
            ref={addButtonRef}
            type="button"
            aria-label={t("drilldown.myBrandKpi.addKpi", "Add KPI")}
            data-testid="add-my-brand-kpi"
            disabled={available.length === 0}
            className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] border border-dashed border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="max-h-[320px] w-[220px] overflow-y-auto p-1">
          {available.map((e) => (
            <Button
              key={e.id}
              variant="ghost"
              size="sm"
              data-testid={`add-my-brand-kpi-option-${e.id}`}
              className="w-full justify-start gap-2 font-normal"
              onClick={() => addMyBrandKpi(e.id)}
            >
              <span className="size-3 shrink-0 rounded-[4px]" style={{ background: e.colorVar }} aria-hidden />
              {e.label}
            </Button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  )
}

function readFlexGap(element: HTMLElement): number {
  const parsed = Number.parseFloat(getComputedStyle(element).columnGap)
  return Number.isFinite(parsed) ? parsed : PILL_ROW_GAP_FALLBACK_PX
}

function readPillWidths(container: HTMLElement): Map<string, number> {
  const widths = new Map<string, number>()
  container.querySelectorAll<HTMLElement>("[data-kpi-pill-id]").forEach((pill) => {
    const id = pill.dataset.kpiPillId
    if (id) widths.set(id, pill.offsetWidth)
  })
  return widths
}

/** Measures each pill once (and again when the set, the labels, or webfonts change),
 *  then keeps later resizes on the cached widths so the row never wraps. */
function useSingleLineKpiIds(ids: readonly string[], labelsKey: string) {
  const containerRef = useRef<HTMLDivElement>(null)
  const addButtonRef = useRef<HTMLButtonElement>(null)
  const widthsRef = useRef<Map<string, number>>(new Map())
  const idsRef = useRef(ids)
  const contentKeyRef = useRef("")
  idsRef.current = ids

  const [measuredKey, setMeasuredKey] = useState("")
  const [visibleCount, setVisibleCount] = useState(ids.length)
  const [remeasureTick, setRemeasureTick] = useState(0)

  const contentKey = `${labelsKey}#${remeasureTick}`
  contentKeyRef.current = contentKey
  const needsMeasure = measuredKey !== contentKey

  const countFitted = useCallback((): number | null => {
    const container = containerRef.current
    const ordered = idsRef.current
    if (!container) return null
    if (ordered.length === 0) return 0

    const cacheComplete = ordered.every((id) => (widthsRef.current.get(id) ?? 0) > 0)
    if (!cacheComplete) {
      const read = readPillWidths(container)
      if (!ordered.every((id) => (read.get(id) ?? 0) > 0)) return null
      widthsRef.current = read
    }
    if (container.clientWidth <= 0) return null

    const gap = readFlexGap(container)
    const addWidth = addButtonRef.current?.offsetWidth ?? 0
    const available = container.clientWidth - addWidth - gap
    return getVisiblePillCount({
      pillWidths: ordered.map((id) => widthsRef.current.get(id) ?? 0),
      containerWidth: available,
      reservedWidth: OVERFLOW_TRIGGER_RESERVE_PX + gap,
      gap,
    })
  }, [])

  const commit = useCallback((count: number, key: string) => {
    setMeasuredKey((current) => (current === key ? current : key))
    setVisibleCount((current) => (current === count ? current : count))
  }, [])

  useLayoutEffect(() => {
    if (needsMeasure) widthsRef.current = new Map()
    const next = countFitted()
    if (next == null) return
    commit(next, contentKey)
  }, [needsMeasure, contentKey, countFitted, commit])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(() => {
      const next = countFitted()
      if (next == null) return
      commit(next, contentKeyRef.current)
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [countFitted, commit])

  useEffect(() => {
    const fonts = document.fonts
    if (!fonts?.ready) return
    let cancelled = false
    // Label fonts that finish loading after the first measure change pill widths.
    void fonts.ready.then(() => {
      if (!cancelled) setRemeasureTick((tick) => tick + 1)
    })
    return () => {
      cancelled = true
    }
  }, [labelsKey])

  const shownIds = needsMeasure ? [...ids] : ids.slice(0, visibleCount)
  const hiddenIds = needsMeasure ? [] : ids.slice(shownIds.length)
  return { containerRef, addButtonRef, shownIds, hiddenIds }
}
